import type { CSSProperties, ReactNode, RefObject } from 'react';
import { cn } from '../../utils/cn';
import { FloatingPanel } from './FloatingPanel';
import { popupList, popupMessage, popupPanel } from './fieldStyles';
import type { Placement } from '../../hooks/useFloating';

export interface ListboxPopupProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  listboxId: string;
  /** Option rows (`<li role="option">`). The listbox is omitted when `optionCount` is 0. */
  children?: ReactNode;
  optionCount: number;
  /** Status row shown above/below the options (loading, empty, error, hints). */
  message?: ReactNode;
  messageTone?: 'muted' | 'danger';
  /** Content pinned above the list (search field, select-all row). */
  header?: ReactNode;
  /** Extra content rendered under the list (footer actions). */
  footer?: ReactNode;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  multiselectable?: boolean;
  matchWidth?: boolean;
  placement?: Placement;
  onClose: () => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * Portalled, collision-aware listbox panel shared by Select, Combobox, MultiSelect and TimePicker.
 * Positioning, the enter/exit animation and dismissal all come from `<FloatingPanel>`.
 */
export function ListboxPopup({
  anchorRef,
  open,
  listboxId,
  children,
  optionCount,
  message,
  messageTone = 'muted',
  header,
  footer,
  multiselectable,
  matchWidth = true,
  placement = 'bottom-start',
  onClose,
  className,
  style,
  ...aria
}: ListboxPopupProps) {
  return (
    <FloatingPanel
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      closeOnEscape={false}
      placement={placement}
      offset={6}
      matchWidth={matchWidth}
      size
      elevation={3}
      surface={2}
      radius="lg"
      // Listboxes are portalled to document.body. They must sit above a Dialog's
      // overlay when the triggering field lives inside a modal.
      zIndex="calc(var(--zo-z-overlay) + 1)"
      style={style}
      className={cn(popupPanel, 'flex max-h-80 flex-col py-1', className)}
      onMouseDown={(e) => e.preventDefault()}
      role="presentation"
      data-zo-popup=""
    >
      {header}
      {optionCount > 0 ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={aria['aria-label']}
          aria-labelledby={aria['aria-labelledby']}
          aria-multiselectable={multiselectable || undefined}
          className={popupList}
        >
          {children}
        </ul>
      ) : null}
      {message != null ? (
        <div role="status" aria-live="polite" className={cn(popupMessage, messageTone === 'danger' && 'text-danger-fg')}>
          {message}
        </div>
      ) : null}
      {footer}
    </FloatingPanel>
  );
}
